import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { customerbasedshift } from '../../Services/app/masterservice';
import { cleanCustomerId } from '../../Services/app/operatorservice';

// Production Orders CRUD — the backlog of jobs the MES Gantt schedules
// onto machines. Stored as customer attribute 'production_orders' (array).
//
// Per design-simplicity rules: color carries priority/status, no paragraphs,
// icons replace labels where unambiguous.

const PRIORITY_COLOR = {
  low:    { bg: '#f3f4f6', text: '#374151', dot: '#9ca3af' },
  normal: { bg: '#eff6ff', text: '#1e40af', dot: '#3b82f6' },
  high:   { bg: '#fef3c7', text: '#92400e', dot: '#f59e0b' },
  rush:   { bg: '#fee2e2', text: '#7a0000', dot: '#dc2626' },
};

const STATUS_COLOR = {
  queue:       { bg: '#f9fafb', text: '#6b7280', icon: '⏳' },
  in_progress: { bg: '#dbeafe', text: '#1e40af', icon: '▶' },
  done:        { bg: '#dcfce7', text: '#14532d', icon: '✓' },
  cancelled:   { bg: '#fee2e2', text: '#7a0000', icon: '✕' },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }); }
  catch { return '—'; }
};
const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }); }
  catch { return '—'; }
};

const newOrderTemplate = () => ({
  order_id: `WO-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 10000)).padStart(4, '0')}`,
  customer_name: '',
  component_number: '',
  qty_total: 100,
  qty_done: 0,
  due_date: (() => { const d = new Date(); d.setDate(d.getDate() + 7); d.setHours(18, 0, 0, 0); return d.toISOString(); })(),
  priority: 'normal',
  status: 'queue',
  preferred_machines: [],
  notes: '',
  assigned_machine: null,
  planned_start: null,
  planned_end: null,
  created_at: Date.now(),
  updated_at: Date.now(),
});

const ProductionOrders = () => {
  const customerId = localStorage.getItem('CustomerID');
  const cid = cleanCustomerId(customerId);
  const SERVER = (window._env_?.SERVER_URL2 || '').replace(/\/$/, '');

  const [orders, setOrders] = useState([]);
  const [components, setComponents] = useState([]); // for component_number autocomplete
  const [machines, setMachines] = useState([]);     // for preferred_machines dropdown
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // order being edited or new
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchAll = async () => {
    if (!cid) return;
    setLoading(true);
    try {
      // Orders via our own endpoint (bypasses TB REST cache, always fresh)
      const [ordersR, compsR, machsR] = await Promise.all([
        axios.get(`${SERVER}/api/buddy/mes/orders`, { params: { customer_id: cid } }).catch(() => ({ data: { orders: [] } })),
        customerbasedshift(cid, 'component'),
        axios.get(`${SERVER}/api/buddy/gcode/programs`, { params: { customer_id: cid } }).catch(() => ({ data: [] })),
      ]);
      setOrders(Array.isArray(ordersR.data?.orders) ? ordersR.data.orders : []);
      const compArr = compsR?.[0]?.value || [];
      setComponents(Array.isArray(compArr) ? compArr : []);
      const machNames = Array.from(new Set((machsR.data || []).map(p => p.device_name).filter(Boolean)));
      setMachines(machNames.sort());
    } catch (e) {
      console.error('fetch orders failed', e);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [cid]);

  const saveAll = async (next) => {
    setSaving(true);
    try {
      // Direct backend write — bypasses TB cache so the list refreshes immediately
      await axios.post(`${SERVER}/api/buddy/mes/orders`, { customer_id: cid, orders: next });
      setOrders(next);
      Swal.fire({ icon: 'success', title: 'Saved', timer: 800, showConfirmButton: false, toast: true, position: 'top-end' });
    } catch (e) {
      Swal.fire('Save failed', e?.response?.data?.message || e?.message || 'Unknown', 'error');
    } finally { setSaving(false); }
  };

  const upsertOrder = async (order) => {
    const others = orders.filter(o => o.order_id !== order.order_id);
    const next = [...others, { ...order, updated_at: Date.now() }];
    await saveAll(next);
    setEditing(null);
  };

  const deleteOrder = async (orderId) => {
    const confirm = await Swal.fire({ icon: 'warning', title: `Delete ${orderId}?`, showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#dc2626' });
    if (!confirm.isConfirmed) return;
    await saveAll(orders.filter(o => o.order_id !== orderId));
  };

  const filteredOrders = useMemo(() => {
    return orders.filter(o => {
      if (statusFilter !== 'all' && o.status !== statusFilter) return false;
      if (searchText) {
        const q = searchText.toLowerCase();
        return (o.order_id || '').toLowerCase().includes(q)
            || (o.customer_name || '').toLowerCase().includes(q)
            || (o.component_number || '').toLowerCase().includes(q);
      }
      return true;
    }).sort((a, b) => {
      // Priority then due date
      const prio = { rush: 0, high: 1, normal: 2, low: 3 };
      const pa = prio[a.priority] ?? 2, pb = prio[b.priority] ?? 2;
      if (pa !== pb) return pa - pb;
      return new Date(a.due_date || 0) - new Date(b.due_date || 0);
    });
  }, [orders, searchText, statusFilter]);

  const counts = useMemo(() => {
    const c = { all: orders.length, queue: 0, in_progress: 0, done: 0, cancelled: 0 };
    for (const o of orders) c[o.status] = (c[o.status] || 0) + 1;
    return c;
  }, [orders]);

  if (!cid) return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>No customer logged in.</div>;

  return (
    <div style={{ padding: '20px 28px 40px', background: 'var(--y-bg-soft, #fafafa)', minHeight: 'calc(100vh - 70px)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, color: '#111827', fontWeight: 800, letterSpacing: -0.3 }}>📦 Production Orders</h2>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>The backlog of jobs · used by the MES Gantt</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            onClick={() => window.location.href = '/mes-planning'}
            style={{ padding: '7px 14px', fontSize: 13, fontWeight: 700, background: 'var(--y-bg, #fff)', color: '#4338ca', border: '1px solid #4338ca', borderRadius: 6, cursor: 'pointer' }}
          >📅 View Gantt</button>
          <button
            onClick={() => setEditing(newOrderTemplate())}
            style={{ padding: '7px 16px', fontSize: 13, fontWeight: 700, background: '#F99022', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', boxShadow: '0 2px 4px rgba(249,144,34,0.3)' }}
          >+ New Order</button>
        </div>
      </div>

      {/* Filter strip */}
      <div style={{ background: 'var(--y-bg, #fff)', border: '1px solid var(--y-border, #e5e7eb)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <input
          type="text" placeholder="Search ID / customer / component…"
          value={searchText} onChange={e => setSearchText(e.target.value)}
          style={{ flex: '0 0 280px', padding: '6px 10px', fontSize: 13, border: '1px solid var(--y-border, #d1d5db)', borderRadius: 5 }}
        />
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {[
            { v: 'all',         label: 'All' },
            { v: 'queue',       label: '⏳ Queue' },
            { v: 'in_progress', label: '▶ Running' },
            { v: 'done',        label: '✓ Done' },
            { v: 'cancelled',   label: '✕ Cancelled' },
          ].map(f => (
            <button key={f.v} onClick={() => setStatusFilter(f.v)} style={{
              padding: '5px 10px', fontSize: 12, fontWeight: 700,
              background: statusFilter === f.v ? '#F99022' : '#fff',
              color: statusFilter === f.v ? '#fff' : '#374151',
              border: '1px solid ' + (statusFilter === f.v ? '#F99022' : '#d1d5db'),
              borderRadius: 999, cursor: 'pointer',
            }}>{f.label} <span style={{ opacity: 0.7 }}>({counts[f.v] ?? 0})</span></button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading && !orders.length && <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading…</div>}
      {!loading && orders.length === 0 && (
        <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af', background: 'var(--y-bg, #fff)', borderRadius: 8, border: '1px dashed #d1d5db' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📦</div>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>No production orders yet</div>
          <div style={{ fontSize: 12 }}>Click <b>+ New Order</b> to add your first one.</div>
        </div>
      )}
      {orders.length > 0 && (
        <div style={{ background: 'var(--y-bg, #fff)', border: '1px solid var(--y-border, #e5e7eb)', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: 'var(--y-bg-soft, #f9fafb)' }}>
              <tr>
                <Th>Order</Th>
                <Th>Customer</Th>
                <Th>Component</Th>
                <Th>Qty</Th>
                <Th>Due</Th>
                <Th>Priority</Th>
                <Th>Status</Th>
                <Th>Assigned</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map(o => {
                const pc = PRIORITY_COLOR[o.priority] || PRIORITY_COLOR.normal;
                const sc = STATUS_COLOR[o.status] || STATUS_COLOR.queue;
                const dueMs = o.due_date ? new Date(o.due_date).getTime() : 0;
                const isOverdue = dueMs && dueMs < Date.now() && o.status !== 'done' && o.status !== 'cancelled';
                const pct = o.qty_total ? Math.round((o.qty_done || 0) / o.qty_total * 100) : 0;
                return (
                  <tr key={o.order_id} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <Td><b style={{ fontFamily: 'monospace', fontSize: 12, color: '#111827' }}>{o.order_id}</b></Td>
                    <Td><span style={{ fontWeight: 600 }}>{o.customer_name || '—'}</span></Td>
                    <Td>
                      <span style={{ background: 'var(--y-bg, #f3f4f6)', padding: '1px 6px', borderRadius: 3, fontFamily: 'monospace', fontSize: 11, fontWeight: 700 }}>{o.component_number || '—'}</span>
                    </Td>
                    <Td>
                      <div style={{ fontSize: 12, fontWeight: 700 }}>{o.qty_done || 0} / {o.qty_total || 0}</div>
                      <div style={{ width: 70, height: 5, background: 'var(--y-bg, #f3f4f6)', borderRadius: 999, marginTop: 3 }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: pct >= 100 ? '#16a34a' : '#3b82f6', borderRadius: 999 }} />
                      </div>
                    </Td>
                    <Td>
                      <span style={{ color: isOverdue ? '#dc2626' : '#374151', fontWeight: isOverdue ? 800 : 600 }}>
                        {isOverdue ? '⚠ ' : ''}{fmtDate(o.due_date)}
                      </span>
                    </Td>
                    <Td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', background: pc.bg, color: pc.text, borderRadius: 999, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: pc.dot }} />{o.priority}
                      </span>
                    </Td>
                    <Td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', background: sc.bg, color: sc.text, borderRadius: 999, fontSize: 11, fontWeight: 800 }}>
                        {sc.icon} {o.status?.replace('_', ' ')}
                      </span>
                    </Td>
                    <Td>
                      {o.assigned_machine
                        ? <><b>{o.assigned_machine}</b><div style={{ fontSize: 10, color: '#6b7280' }}>{fmtDateTime(o.planned_start)}</div></>
                        : <span style={{ color: '#9ca3af', fontSize: 11, fontStyle: 'italic' }}>not scheduled</span>
                      }
                    </Td>
                    <Td>
                      <button onClick={() => setEditing(o)} style={btnStyle(false)}>✎</button>
                      <button onClick={() => deleteOrder(o.order_id)} style={{ ...btnStyle(false), color: '#dc2626', marginLeft: 4 }}>🗑</button>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <OrderEditModal
          order={editing}
          components={components}
          machines={machines}
          onSave={upsertOrder}
          onClose={() => setEditing(null)}
          saving={saving}
        />
      )}
    </div>
  );
};

const Th = ({ children }) => <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 11, fontWeight: 700, color: '#374151', letterSpacing: 0.4, textTransform: 'uppercase' }}>{children}</th>;
const Td = ({ children }) => <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>{children}</td>;
const btnStyle = (primary) => ({ padding: '4px 8px', fontSize: 12, fontWeight: 700, background: primary ? '#F99022' : '#fff', color: primary ? '#fff' : '#374151', border: '1px solid ' + (primary ? '#F99022' : '#d1d5db'), borderRadius: 4, cursor: 'pointer' });

// ============================================================
// Order Edit Modal
// ============================================================
const OrderEditModal = ({ order, components, machines, onSave, onClose, saving }) => {
  const [f, setF] = useState(order);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const togglePreferred = (m) => {
    const cur = f.preferred_machines || [];
    set('preferred_machines', cur.includes(m) ? cur.filter(x => x !== m) : [...cur, m]);
  };
  const dueLocalValue = f.due_date ? new Date(f.due_date).toISOString().slice(0, 16) : '';

  const handleSave = () => {
    if (!f.order_id) { Swal.fire('Order ID required', '', 'info'); return; }
    if (!f.component_number) { Swal.fire('Component required', '', 'info'); return; }
    if (!f.qty_total || f.qty_total <= 0) { Swal.fire('Qty must be > 0', '', 'info'); return; }
    onSave(f);
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--y-bg, #fff)', borderRadius: 10, padding: 22, width: 600, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid var(--y-border, #e5e7eb)' }}>
          <h3 style={{ margin: 0, fontSize: 17, color: '#111827' }}>📦 {order.order_id ? 'Edit Order' : 'New Order'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280' }}>✕</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <Field label="Order ID">
            <input value={f.order_id || ''} onChange={e => set('order_id', e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Customer">
            <input value={f.customer_name || ''} onChange={e => set('customer_name', e.target.value)} placeholder="e.g. TVS Motors" style={inputStyle} />
          </Field>
          <Field label="Component">
            <select value={f.component_number || ''} onChange={e => set('component_number', e.target.value)} style={inputStyle}>
              <option value="">— pick component —</option>
              {components.map(c => (
                <option key={c.component_number} value={c.component_number}>
                  {c.component_number} — {c.component_name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Qty total">
            <input type="number" min={1} value={f.qty_total || 0} onChange={e => set('qty_total', parseInt(e.target.value, 10) || 0)} style={inputStyle} />
          </Field>
          <Field label="Qty done">
            <input type="number" min={0} value={f.qty_done || 0} onChange={e => set('qty_done', parseInt(e.target.value, 10) || 0)} style={inputStyle} />
          </Field>
          <Field label="Due date">
            <input type="datetime-local" value={dueLocalValue} onChange={e => set('due_date', e.target.value ? new Date(e.target.value).toISOString() : null)} style={inputStyle} />
          </Field>
          <Field label="Priority">
            <select value={f.priority || 'normal'} onChange={e => set('priority', e.target.value)} style={inputStyle}>
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="rush">⚠ Rush</option>
            </select>
          </Field>
          <Field label="Status">
            <select value={f.status || 'queue'} onChange={e => set('status', e.target.value)} style={inputStyle}>
              <option value="queue">⏳ Queue</option>
              <option value="in_progress">▶ Running</option>
              <option value="done">✓ Done</option>
              <option value="cancelled">✕ Cancelled</option>
            </select>
          </Field>
        </div>
        <Field label="Preferred machines (optional — auto-planner respects this)">
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {(machines || []).map(m => {
              const on = (f.preferred_machines || []).includes(m);
              return (
                <button key={m} type="button" onClick={() => togglePreferred(m)} style={{
                  padding: '4px 10px', fontSize: 12, fontWeight: 700,
                  background: on ? '#F99022' : '#fff',
                  color: on ? '#fff' : '#374151',
                  border: '1px solid ' + (on ? '#F99022' : '#d1d5db'),
                  borderRadius: 999, cursor: 'pointer',
                }}>{m}</button>
              );
            })}
            {(!machines || machines.length === 0) && <span style={{ fontSize: 11, color: '#9ca3af' }}>No machines in telemetry yet</span>}
          </div>
        </Field>
        <Field label="Assigned machine (manual override)">
          <select value={f.assigned_machine || ''} onChange={e => set('assigned_machine', e.target.value || null)} style={inputStyle}>
            <option value="">— none / let auto-planner decide —</option>
            {(machines || []).map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Planned start">
            <input type="datetime-local" value={f.planned_start ? new Date(f.planned_start).toISOString().slice(0, 16) : ''}
              onChange={e => set('planned_start', e.target.value ? new Date(e.target.value).toISOString() : null)} style={inputStyle} />
          </Field>
          <Field label="Planned end (auto-computed if blank)">
            <input type="datetime-local" value={f.planned_end ? new Date(f.planned_end).toISOString().slice(0, 16) : ''}
              onChange={e => set('planned_end', e.target.value ? new Date(e.target.value).toISOString() : null)} style={inputStyle} />
          </Field>
        </div>
        <Field label="Notes">
          <textarea value={f.notes || ''} onChange={e => set('notes', e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
        </Field>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button onClick={onClose} style={{ padding: '8px 16px', fontSize: 13, background: 'var(--y-bg, #fff)', color: '#6b7280', border: '1px solid var(--y-border, #d1d5db)', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: '8px 18px', fontSize: 13, fontWeight: 800, background: '#F99022', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>{saving ? 'Saving…' : '✓ Save'}</button>
        </div>
      </div>
    </div>
  );
};

const inputStyle = { width: '100%', padding: '7px 10px', fontSize: 13, border: '1px solid var(--y-border, #d1d5db)', borderRadius: 5, fontFamily: 'inherit' };
const Field = ({ label, children }) => (
  <label style={{ display: 'block', marginBottom: 10 }}>
    <span style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</span>
    {children}
  </label>
);

export default ProductionOrders;
